<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { MapEngine, type Country, type TextureTier } from '@atlasguess/engine';
  import DebugPanel from './DebugPanel.svelte';

  let container: HTMLDivElement | undefined;
  let engine: MapEngine | undefined = $state();
  let fps = $state(0);
  let tier: TextureTier | 'loading' = $state('loading');
  let lastLoadMs = $state(0);
  let gpuMax = $state(0);
  let revealed = $state(false);
  let debugOpen = $state(false);
  let hoveredCountry = $state<Country | null>(null);
  let lastClickedCountry = $state<Country | null>(null);
  let pollInterval: ReturnType<typeof setInterval> | undefined;

  onMount(() => {
    if (!container) return;
    engine = new MapEngine({
      container,
      onTextureLoad: ({ tier: loadedTier, durationMs }) => {
        tier = loadedTier;
        lastLoadMs = Math.round(durationMs);
        if (!revealed) {
          setTimeout(() => {
            revealed = true;
          }, 80);
        }
        console.info(`[atlas] ${loadedTier} texture ready in ${Math.round(durationMs)}ms`);
      },
      onTextureError: (err, failedTier) => {
        console.warn(`[atlas] texture tier ${failedTier} failed`, err);
      },
      onCountryHover: (country) => {
        hoveredCountry = country;
      },
      onCountryClick: (country) => {
        lastClickedCountry = country;
        if (country) {
          console.info(`[atlas] click: ${country.name} (${country.iso_a2})`);
        }
      },
    });
    gpuMax = engine.getGpuMaxTextureSize();
    pollInterval = setInterval(() => {
      if (engine) fps = engine.getFPS();
    }, 250);

    // Polygons are NOT loaded on demo init — first paint stays minimal
    // (texture only). Toggling Country borders / Country fills / Small-country
    // dots in the debug panel triggers loadBorders() lazily. Real game modes
    // call engine.loadBorders() themselves at the moment their gameplay needs
    // hit-testing or borders.
  });

  onDestroy(() => {
    if (pollInterval) clearInterval(pollInterval);
    engine?.dispose();
  });
</script>

<div class="globe">
  <div class="globe__canvas" bind:this={container}></div>

  <div class="globe__loading" class:globe__loading--hidden={revealed}>
    <div class="globe__loading-spinner" aria-hidden="true"></div>
    <div class="globe__loading-text">Loading globe</div>
  </div>

  <div class="globe__hud" class:globe__hud--hidden={!revealed}>
    <div class="globe__hud-row">
      <span class="globe__hud-label">FPS</span>
      <span class="globe__hud-value">{fps}</span>
    </div>
    <div class="globe__hud-row">
      <span class="globe__hud-label">TEX</span>
      <span class="globe__hud-value">{tier}</span>
    </div>
    {#if hoveredCountry}
      <div class="globe__hud-row globe__hud-row--country">
        <span class="globe__hud-country">{hoveredCountry.name}</span>
      </div>
    {/if}
  </div>

  <button
    class="globe__debug-toggle"
    class:globe__debug-toggle--hidden={!revealed || debugOpen}
    onclick={() => (debugOpen = true)}
    aria-label="Open debug panel"
  >
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="3" cy="7" r="1.2" fill="currentColor" />
      <circle cx="7" cy="7" r="1.2" fill="currentColor" />
      <circle cx="11" cy="7" r="1.2" fill="currentColor" />
    </svg>
  </button>

  <DebugPanel
    {engine}
    open={debugOpen}
    onClose={() => (debugOpen = false)}
    currentTier={tier}
    {lastLoadMs}
    gpuMaxTextureSize={gpuMax}
    {hoveredCountry}
    {lastClickedCountry}
  />
</div>
